import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  memo,
} from "react";
import "@sendbird/uikit-react/dist/index.css";
import { useRecoilState, useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import {
  CHAT_DETAIL_ENDPOINT,
  CHAT_DOMAIN,
  CHAT_READ_MESSAGE_ENDPOINT,
  chatApi,
} from "api/chatApi";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GroupChannel } from "@sendbird/uikit-react/GroupChannel";
import { userWebChatAtom } from "recoil/userWebChatAtom";
import { MessageList } from "@sendbird/uikit-react/GroupChannel/components/MessageList";
import { Message } from "@sendbird/uikit-react/GroupChannel/components/Message";
import {
  AspectRatio,
  Avatar,
  Box,
  Button,
  GlobalStyles,
  Link,
  Sheet,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { RenderCustomSeparatorProps } from "@sendbird/uikit-react/types/types";
import ModalDialog from "@mui/joy/ModalDialog";
import Modal from "@mui/joy/Modal";
import { useSendbirdStateContext } from "@sendbird/uikit-react";
import { useNavigate } from "react-router-dom";
import { useGroupChannelContext } from "@sendbird/uikit-react/GroupChannel/context";
import { theme } from "styles/theme";
import { extractHexColor } from "../../../styles/hexOpacity";

// =================================================================
// 1. 유틸리티 함수 (Utils)
// =================================================================

const isSameMessage = (a: any, b: any): boolean => {
  if (!a || !b) return false;
  return (
    (a.messageId && b.messageId && a.messageId === b.messageId) ||
    (a.reqId && b.reqId && a.reqId === b.reqId) ||
    (!a.messageId && !b.messageId && a.createdAt === b.createdAt)
  );
};

const areMessagesChained = (currentMessage: any, prevMessage: any): boolean => {
  if (!currentMessage || !prevMessage) return false;
  const isNormalMessage = (msg: any) =>
    msg.isUserMessage?.() || msg.isFileMessage?.();
  if (!isNormalMessage(currentMessage) || !isNormalMessage(prevMessage))
    return false;
  if (currentMessage.sender?.userId !== prevMessage.sender?.userId)
    return false;
  const TIME_THRESHOLD = 60 * 1000; // 1분
  if (currentMessage.createdAt - prevMessage.createdAt > TIME_THRESHOLD)
    return false;
  return true;
};

const formatTime = (createdAt: number): string => {
  if (!createdAt) return "";
  const d = new Date(createdAt);
  if (isNaN(d.getTime())) return "";
  const h = d.getHours();
  const m = d.getMinutes();
  return `${h}:${m.toString().padStart(2, "0")}`;
};

// =================================================================
// 2. 마지막 메시지 상태 관리를 위한 Context
// =================================================================

type LastMsgCtx = { lastMessage: any | null; lastKey: number | string };
const LastMessageContext = createContext<LastMsgCtx | null>(null);

function LastMessageProvider({ children }: { children: React.ReactNode }) {
  const { messages, currentChannel } = useGroupChannelContext();
  const lastMessage: any =
    messages.length > 0
      ? messages[messages.length - 1]
      : (currentChannel?.lastMessage ?? null);
  const lastKey =
    lastMessage?.messageId ?? lastMessage?.reqId ?? lastMessage?.createdAt ?? 0;
  const value = useMemo(
    () => ({ lastMessage, lastKey }),
    [lastMessage, lastKey]
  );

  return (
    <LastMessageContext.Provider value={value}>
      {children}
    </LastMessageContext.Provider>
  );
}

function useLastMessage() {
  return useContext(LastMessageContext);
}

// =================================================================
// 3. 커스텀 메시지 렌더링 컴포넌트
// =================================================================

const ClickableText = memo(({ text }: { text: string }) => {
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
  const parts = text.split(urlRegex);
  return (
    <Typography
      level="body-sm"
      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", color: "inherit" }}
    >
      {parts.map((part, index) => {
        if (part.match(urlRegex)) {
          const href = part.startsWith("www.") ? `https://${part}` : part;
          return (
            <Link
              key={index}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{ color: "inherit" }}
            >
              {part}
            </Link>
          );
        }
        return part;
      })}
    </Typography>
  );
});

const OGMessagePreview = memo(({ ogMetaData }: { ogMetaData: any }) => {
  if (!ogMetaData?.url) return null;
  return (
    <Link
      href={ogMetaData.url}
      target="_blank"
      rel="noopener noreferrer"
      underline="none"
      sx={{ display: "block" }}
    >
      <Sheet
        variant="outlined"
        sx={{
          maxWidth: "320px",
          minWidth: "320px",
          borderRadius: "md",
          overflow: "hidden",
          borderWidth: 0,
          bgcolor: "background.surface",
        }}
      >
        {ogMetaData.defaultImage?.url && (
          <AspectRatio ratio="1.91/1">
            <img
              src={ogMetaData.defaultImage.url}
              alt={ogMetaData.defaultImage.alt || "og-image"}
            />
          </AspectRatio>
        )}
        <Box sx={{ p: "10px" }}>
          <Typography
            level="title-sm"
            sx={{
              mb: 0.5,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {ogMetaData.title}
          </Typography>
          <Typography
            level="body-xs"
            sx={{
              color: theme.vars.palette.Content.Weak,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minHeight: "2.5em",
            }}
          >
            {ogMetaData.description}
          </Typography>
          <Typography
            level="body-xs"
            sx={{
              color: theme.vars.palette.Content.Weak,
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {ogMetaData.url}
          </Typography>
        </Box>
      </Sheet>
    </Link>
  );
});

const LinkedTextMessage = memo(
  ({
    text,
    linkLabel,
    linkPath,
  }: {
    text: string;
    linkLabel: string;
    linkPath: string;
  }) => {
    const navigate = useNavigate();
    const idx = text.lastIndexOf(linkLabel);
    if (idx === -1) {
      return <ClickableText text={text} />;
    }
    const before = text.slice(0, idx);
    const after = text.slice(idx + linkLabel.length);

    return (
      <Typography
        level="body-sm"
        sx={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          color: "inherit",
        }}
      >
        {before}
        <Box
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            navigate(linkPath);
          }}
          sx={{
            cursor: "pointer",
            textDecoration: "underline",
            textUnderlineOffset: "2px",
            color: "inherit",
          }}
        >
          {linkLabel}
        </Box>
        {after}
      </Typography>
    );
  }
);

const MessageBody = memo(({ message }: { message: any }) => {
  if (message.isFileMessage?.() && message.type.startsWith("image/")) {
    return (
      <Link href={message.url} target="_blank" rel="noopener noreferrer">
        <img
          src={message.url}
          alt={message.name || "image"}
          style={{
            maxWidth: "80%",
            height: "auto",
            borderRadius: "16px",
            display: "block",
          }}
        />
      </Link>
    );
  }
  if (message.isUserMessage?.() && message.customType === "linked_text") {
    try {
      const parsed = JSON.parse(message.data || "{}");
      if (parsed?.linkLabel && parsed?.linkPath) {
        return (
          <LinkedTextMessage
            text={message.message}
            linkLabel={parsed.linkLabel}
            linkPath={parsed.linkPath}
          />
        );
      }
    } catch {
      // malformed data → fall through to default rendering
    }
  }
  if (message.isUserMessage?.() && message.ogMetaData) {
    return (
      <Stack>
        <ClickableText text={message.message} />
        <OGMessagePreview ogMetaData={message.ogMetaData} />
      </Stack>
    );
  }
  if (message.isUserMessage?.()) {
    return <ClickableText text={message.message} />;
  }
  return null;
});

const BaseMessageContent: React.FC<any> = memo((props) => {
  const { message, shouldShowTimestamp } = props;
  if (!message?.sender) return null;
  const isImageMessage =
    message.isFileMessage?.() && message.type.startsWith("image/");
  const hasOG = message.isUserMessage?.() && message.ogMetaData;

  // 타임스탬프가 있는 경우를 고려해 공간을 예약
  const reserveTimeWidth = 44; // HH:mm + 여백 대략치
  const bubbleMaxWidth =
    shouldShowTimestamp && !isImageMessage && !hasOG
      ? `calc(70% - ${reserveTimeWidth}px)`
      : "70%";

  return (
    <Stack direction="row" spacing={1} alignItems="flex-end">
      <Avatar
        size="sm"
        src={message.sender.profileUrl}
        alt={message.sender.nickname || "User"}
        sx={{ width: "28px", height: "28px" }}
      >
        {message.sender.nickname?.charAt(0)}
      </Avatar>
      <Stack
        direction="row"
        spacing={"4px"}
        alignItems="flex-end"
        sx={{ flex: 1, minWidth: 0 }} // ← 행이 수축될 때 정상 줄바꿈
      >
        {hasOG ? (
          <Stack
            direction="column"
            alignItems="flex-start"
            spacing={0.5}
            sx={{ maxWidth: "70%", minWidth: 0 }}
          >
            <Stack
              direction="row"
              alignItems="flex-end"
              spacing={"4px"}
              sx={{ width: "100%", minWidth: 0 }}
            >
              <Box
                sx={{
                  p: "8px 12px",
                  borderRadius: "16px",
                  bgcolor: "white",
                  color: "neutral.900",
                  maxWidth: "100%",
                  minWidth: 0,
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                }}
              >
                <ClickableText text={message.message} />
              </Box>
              {shouldShowTimestamp && !isImageMessage && (
                <Typography
                  variant={"Footnote"}
                  sx={{
                    color: theme.vars.palette.Content.Weak,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {formatTime(message.createdAt)}
                </Typography>
              )}
            </Stack>
            <OGMessagePreview ogMetaData={message.ogMetaData} />
          </Stack>
        ) : (
          <>
            <Box
              sx={{
                p: isImageMessage ? 0 : "8px 12px",
                borderRadius: "16px",
                maxWidth: bubbleMaxWidth, // ← 타임스탬프 공간 예약 반영
                bgcolor: isImageMessage ? "transparent" : "white",
                color: "neutral.900",
                overflow: isImageMessage ? "hidden" : "visible",
                minWidth: 0,
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
              }}
            >
              <MessageBody message={message} />
            </Box>
            {shouldShowTimestamp && !isImageMessage && !hasOG && (
              <Typography
                variant={"Footnote"}
                sx={{
                  color: theme.vars.palette.Content.Weak,
                  whiteSpace: "nowrap",
                  flexShrink: 0, // ← 타임스탬프는 줄어들지 않도록
                }}
              >
                {formatTime(message.createdAt)}
              </Typography>
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
});

const MyMessageContent: React.FC<any> = memo((props) => {
  const { currentChannel: channel } = useGroupChannelContext();
  const { lastMessage } = useLastMessage() || {};
  const { message, shouldShowTimestamp } = props;
  let statusText: string | null = null;
  let statusColor = extractHexColor(theme.vars.palette.Content.Weak);

  if (message && channel) {
    const sendingStatus = (message.sendingStatus || "").toLowerCase();
    if (sendingStatus === "pending") {
      // Pending 상태는 텍스트 없음
    } else if (sendingStatus === "failed") {
      statusText = "전송 실패";
      statusColor = extractHexColor(
        theme.vars.palette.Content.HighlightWithAlert
      );
    } else {
      const unreadCount = channel.getUnreadMemberCount(message);
      statusText = unreadCount === 0 ? "읽음" : "전송됨";
    }
  }

  const shouldShowStatus = Boolean(
    statusText &&
      (isSameMessage(message, lastMessage) ||
        message.sendingStatus === "failed")
  );
  const isImageMessage =
    message.isFileMessage?.() && message.type.startsWith("image/");
  const hasOG = message.isUserMessage?.() && message.ogMetaData;

  // 타임스탬프가 있는 경우를 고려해 공간을 예약
  const reserveTimeWidth = 44;
  const bubbleMaxWidth =
    shouldShowTimestamp && !isImageMessage && !hasOG
      ? `calc(70% - ${reserveTimeWidth}px)`
      : "70%";

  return (
    <Stack direction="column" alignItems="flex-end" spacing={0.5}>
      <Stack
        direction="row"
        spacing={"4px"}
        alignItems="flex-end"
        justifyContent="flex-end"
        sx={{ width: "100%", minWidth: 0 }} // ← 수축 시 줄바꿈 보호
      >
        {shouldShowTimestamp && !isImageMessage && (
          <Typography
            variant={"Footnote"}
            sx={{
              color: theme.vars.palette.Content.Weak,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {formatTime(message.createdAt)}
          </Typography>
        )}
        {hasOG ? (
          <Stack
            direction="column"
            alignItems="flex-end"
            spacing={0.5}
            sx={{ maxWidth: "70%", minWidth: 0 }}
          >
            <Box
              sx={{
                p: "8px 12px",
                borderRadius: "16px",
                bgcolor: theme.vars.palette.Surface.Highlight,
                color: "white",
                maxWidth: "100%",
                minWidth: 0,
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
              }}
            >
              <ClickableText text={message.message} />
            </Box>
            <OGMessagePreview ogMetaData={message.ogMetaData} />
          </Stack>
        ) : (
          <Box
            sx={{
              p: isImageMessage ? 0 : "8px 12px",
              borderRadius: "16px",
              maxWidth: bubbleMaxWidth, // ← 타임스탬프 공간 예약 반영
              bgcolor: isImageMessage
                ? "transparent"
                : theme.vars.palette.Surface.Highlight,
              color: "white",
              overflow: isImageMessage ? "hidden" : "visible",
              minWidth: 0,
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          >
            <MessageBody message={message} />
          </Box>
        )}
      </Stack>
      {(shouldShowStatus || (isImageMessage && shouldShowTimestamp)) && (
        <Stack direction="row" spacing={1} alignItems="center">
          {shouldShowStatus && (
            <Typography
              variant={"Footnote"}
              sx={{ px: "4px", color: statusColor }}
            >
              {statusText}
            </Typography>
          )}
          {isImageMessage && shouldShowTimestamp && (
            <Typography
              variant={"Footnote"}
              sx={{ color: theme.vars.palette.Content.Weak }}
            >
              {formatTime(message.createdAt)}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
});

const RenderMessage: React.FC<any> = memo((props) => {
  const sbState = useSendbirdStateContext();
  const { message, prevMessage, chainTop, chainBottom } = props;
  const currentUserId = sbState?.stores?.userStore?.user?.userId;
  const isByMe = message?.sender?.userId === currentUserId;
  const shouldShowTimestamp = !chainBottom;
  const isChained =
    chainTop ||
    (message.sendingStatus === "pending" &&
      areMessagesChained(message, prevMessage));

  return (
    <Box
      sx={{
        marginTop: isChained ? "4px" : "16px",
        marginBottom: "8px",
      }}
    >
      <Message {...props}>
        {isByMe ? (
          <MyMessageContent
            {...props}
            shouldShowTimestamp={shouldShowTimestamp}
          />
        ) : (
          <BaseMessageContent
            {...props}
            shouldShowTimestamp={shouldShowTimestamp}
          />
        )}
      </Message>
    </Box>
  );
});

// =================================================================
// 4. 커스텀 메시지 입력 컴포넌트
// =================================================================

function CustomMessageInput() {
  const [message, setMessage] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const userWebChatState = useRecoilValue(userWebChatAtom);
  const navigate = useNavigate();
  const { sendUserMessage, scrollToBottom } = useGroupChannelContext();
  const isSendable = message.trim().length > 0;

  const handleSendMessage = async () => {
    if (!isSendable) return;
    if (!userWebChatState?.isSendAvailable) {
      setOpenModal(true);
      return;
    }
    try {
      await sendUserMessage({ message: message.trim() });
      setMessage("");
      // Ensure we actually land at the very bottom. Some message types (images/OG/status)
      // can change height after initial render, so scroll again on the next frame and
      // once more after a short delay.
      scrollToBottom(true);
      if (typeof window !== "undefined") {
        requestAnimationFrame(() => scrollToBottom(true));
        setTimeout(() => scrollToBottom(true), 300);
      }
    } catch (error: any) {
      console.error("Message send failed:", error);
      alert("메시지 전송에 실패했습니다. 다시 시도해 주세요.");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isSendable && !e.shiftKey && e.key === "Enter") {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <ModalDialog
          aria-labelledby="nested-modal-title"
          aria-describedby="nested-modal-description"
        >
          <Typography id="nested-modal-title" level="h2" textAlign={"start"}>
            결제하고 이용해주세요
          </Typography>
          <Typography
            id="nested-modal-description"
            textAlign={"start"}
            textColor="text.tertiary"
          >
            다시 결제하고 프로그램을 이어가세요
          </Typography>
          <Button
            variant="solid"
            size="lg"
            sx={{ mt: 2 }}
            onClick={(e) => {
              e.stopPropagation();
              setOpenModal(false);
              navigate("/payment", { replace: true });
            }}
          >
            계속 이용하기
          </Button>
        </ModalDialog>
      </Modal>
      <Box
        sx={{
          width: "100%",
          mx: "auto",
          px: "16px",
          "@media (min-width: 662px)": {
            px: "calc((100% - 660px) / 2) !important",
          },
          "@media (max-width: 660px)": { px: "16px !important" },
          py: 1,
          backgroundColor: "background.level1",
        }}
      >
        <Stack direction="row" alignItems="end" spacing={1}>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            sx={{
              flex: 1,
              borderColor: "background.level3",
              backgroundColor: "background.level3",
              "--joy-focus-thickness": "0px",
            }}
            minRows={1}
            maxRows={4}
          />
          <Button
            size="sm"
            disabled={!isSendable}
            sx={{ px: "10px" }}
            onClick={handleSendMessage}
          >
            <span className="material-symbols-rounded">send</span>
          </Button>
        </Stack>
      </Box>
    </>
  );
}

// =================================================================
// 5. 메인 컴포넌트
// =================================================================

export default function ChatPatientContent() {
  const user = useRecoilValue(userAtom);
  const [userWebChatState, setUserWebChatState] =
    useRecoilState(userWebChatAtom);
  const [openModal, setOpenModal] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { isLoading } = useQuery(
    [CHAT_DOMAIN, CHAT_DETAIL_ENDPOINT, user?.userId],
    () => chatApi.getDetail(),
    {
      enabled: !!user?.userId && user.role === "patient",
      refetchIntervalInBackground: false,
      onSuccess: (data) => data?.result && setUserWebChatState(data.result),
    }
  );

  useQuery([CHAT_DOMAIN, CHAT_READ_MESSAGE_ENDPOINT], () => chatApi.read(), {
    enabled: !!user?.userId,
    onSuccess: () => {
      queryClient.invalidateQueries([
        CHAT_DOMAIN,
        CHAT_DETAIL_ENDPOINT,
        user?.userId,
      ]);
    },
  });

  useEffect(() => {
    if (!isLoading && !userWebChatState?.chatConnection?.channelUrl) {
      setOpenModal(true);
    }
  }, [userWebChatState, isLoading]);

  const renderMessageCallback = useCallback(
    (props: any) => <RenderMessage {...props} />,
    []
  );
  const renderMessageListCallback = useCallback(
    (props: any) => (
      <LastMessageProvider>
        <MessageList {...props} />
      </LastMessageProvider>
    ),
    []
  );
  const renderMessageInputCallback = useCallback(
    () => <CustomMessageInput />,
    []
  );

  if (!userWebChatState?.chatConnection?.channelUrl) {
    return (
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <ModalDialog
          aria-labelledby="nested-modal-title"
          aria-describedby="nested-modal-description"
        >
          <Typography id="nested-modal-title" level="h2" textAlign={"center"}>
            프로그램을 시작하고 이용해주세요
          </Typography>
          <Typography
            id="nested-modal-description"
            textAlign={"center"}
            textColor="text.tertiary"
          >
            코치 선생님과 프로그램을 시작하세요
          </Typography>
          <Button
            variant="solid"
            size="lg"
            sx={{ mt: 2 }}
            onClick={(e) => {
              e.stopPropagation();
              setOpenModal(false);
              navigate("/payment", { replace: true });
            }}
          >
            프로그램 시작하기
          </Button>
        </ModalDialog>
      </Modal>
    );
  }

  return (
    <Box style={{ height: "100%", width: "100%" }}>
      <GlobalStyles
        styles={{
          ".sendbird-conversation, .sendbird-chat-header, .sendbird-message-input-wrapper":
            { border: "none !important" },
          ".sendbird-conversation__footer": {
            margin: "0 !important",
            paddingBottom: "20px !important",
          },
          ".sendbird-message-content__right": { display: "none !important" },
          ".sendbird-message-content": { padding: "0 !important" },
          "@media (min-width: 662px)": {
            ".sendbird-conversation__messages-padding, .sendbird-message-input-wrapper":
              {
                paddingLeft: "calc((100% - 660px) / 2) !important",
                paddingRight: "calc((100% - 660px) / 2) !important",
              },
          },
          "@media (max-width: 660px)": {
            ".sendbird-conversation__messages-padding, .sendbird-message-input-wrapper":
              {
                paddingLeft: "16px !important",
                paddingRight: "16px !important",
              },
          },
          ".sendbird-conversation__scroll-bottom-button": { display: "none" },
        }}
      />
      <GroupChannel
        channelUrl={userWebChatState.chatConnection.channelUrl}
        renderChannelHeader={() => <></>}
        renderMessage={renderMessageCallback}
        renderMessageList={renderMessageListCallback}
        renderMessageInput={renderMessageInputCallback}
        renderCustomSeparator={(props: RenderCustomSeparatorProps) => {
          const date = new Date(props.message.createdAt);
          const formattedDate = date.toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "long",
            day: "numeric",
            weekday: "long",
          });
          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                my: 2,
              }}
            >
              <Typography
                variant={"Footnote"}
                style={{
                  color: "#ffffff",
                  textAlign: "center",
                  fontWeight: 500,
                }}
                sx={{
                  backgroundColor: theme.vars.palette.Content.SuperWeak,
                  borderRadius: "16px",
                  px: "12px",
                  py: "4px",
                }}
              >
                {formattedDate}
              </Typography>
            </Box>
          );
        }}
        isReactionEnabled={false}
        isMultipleFilesMessageEnabled={false}
        disableUserProfile={true}
        replyType={"NONE"}
      />
    </Box>
  );
}
